import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import {
  Button,
  ClipboardButton,
  DefinitionList,
  DropdownMenu,
  Icon,
  Label,
  Tab,
  TabList,
  TabProvider,
  Table,
  Text,
} from '@gravity-ui/uikit';
import { Ellipsis, Gift, Link as LinkIcon, Pencil, Percent, Plus } from '@gravity-ui/icons';
import type { ManagerBonusView, ManagerCardResponse } from '@odonix/contracts';
import { useManagersApi } from '../../lib/api/managers-context.js';
import { count, day, moment, monthTitle, percent, rub, rubCompact } from '../../ui/format.js';
import { MetricStrip } from '../../ui/metric-strip.js';
import { MonthChart } from '../../ui/month-chart.js';
import { Page, Section } from '../../ui/page.js';
import { PeriodPicker } from '../../ui/period-picker.js';
import { periodOf, periodQuery, type Period } from '../../ui/period.js';
import { RouterLink, useGo } from '../../ui/router-link.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';
import { KIND_LABEL, STATE_LABEL, STATE_TONE, paymentKind } from '../tenants/labels.js';
import { AuditFeed } from './audit-feed.js';
import { MANAGER_STATUS_LABEL, MANAGER_STATUS_THEME } from './labels.js';
import {
  AssignTenantsDialog,
  BonusDialog,
  CancelBonusDialog,
  CommissionDialog,
  DeleteDialog,
  ToggleDialog,
} from './manager-dialogs.js';
import { ManagerFormDialog } from './manager-form.js';

/**
 * Страница менеджера: показатели за период, его клиники, начисления и премии,
 * реферальные ссылки и журнал — и всё, что с ним делает сотрудник платформы.
 * Вкладка в адресе: на «Начисления октября» можно сослаться.
 */

const routeApi = getRouteApi('/shell/managers/$managerId');

type TabId = 'overview' | 'tenants' | 'earnings' | 'links' | 'actions';
type DialogId = 'edit' | 'commission' | 'bonus' | 'assign' | 'toggle' | 'delete';

export function ManagerScreen({ managerId }: { managerId: string }) {
  const api = useManagersApi();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const go = useGo();
  const period = periodOf(search);
  const tab: TabId = search.tab ?? 'overview';
  const [dialog, setDialog] = useState<DialogId | null>(null);
  const [cancelling, setCancelling] = useState<ManagerBonusView | null>(null);

  const card = useQuery({
    queryKey: ['manager-card', managerId, period.from, period.to],
    queryFn: () => api.card(managerId, period),
    placeholderData: (previous) => previous,
  });

  const data = card.data;
  const manager = data?.manager;
  const deleted = manager?.status === 'deleted';
  const close = () => {
    setDialog(null);
  };

  return (
    <Page
      title={manager?.fullName ?? 'Менеджер'}
      crumbs={[{ label: 'Менеджеры', to: `/managers?${periodQuery(period)}` }]}
      aside={
        manager === undefined ? null : (
          <>
            <Label theme={MANAGER_STATUS_THEME[manager.status]} size="m">
              {MANAGER_STATUS_LABEL[manager.status]}
            </Label>
            <Label theme="clear" size="m">
              {`${percent(manager.commissionPercent)} от подписки`}
            </Label>
          </>
        )
      }
      actions={
        manager === undefined || deleted ? (
          <PeriodPicker
            value={period}
            onChange={(next) => {
              void navigate({ search: (prev) => ({ ...prev, from: next.from, to: next.to }) });
            }}
          />
        ) : (
          <>
            <PeriodPicker
              value={period}
              onChange={(next) => {
                void navigate({ search: (prev) => ({ ...prev, from: next.from, to: next.to }) });
              }}
            />
            <Button
              view="outlined"
              onClick={() => {
                setDialog('commission');
              }}
            >
              <Icon data={Percent} size={16} />
              Процент
            </Button>
            <Button
              view="action"
              onClick={() => {
                setDialog('bonus');
              }}
            >
              <Icon data={Gift} size={16} />
              Премия
            </Button>
            <DropdownMenu
              renderSwitcher={(props) => (
                <Button {...props} view="outlined" aria-label="Ещё действия">
                  <Icon data={Ellipsis} size={16} />
                </Button>
              )}
              items={[
                {
                  text: 'Изменить данные',
                  iconStart: <Icon data={Pencil} size={16} />,
                  action: () => {
                    setDialog('edit');
                  },
                },
                {
                  text: 'Закрепить клиники',
                  iconStart: <Icon data={Plus} size={16} />,
                  action: () => {
                    setDialog('assign');
                  },
                },
                [
                  {
                    text: manager.status === 'active' ? 'Отключить' : 'Включить',
                    action: () => {
                      setDialog('toggle');
                    },
                  },
                  {
                    text: 'Удалить менеджера',
                    theme: 'danger',
                    action: () => {
                      setDialog('delete');
                    },
                  },
                ],
              ]}
            />
          </>
        )
      }
    >
      {card.isError ? (
        <ErrorNote title="Карточка не загрузилась" error={card.error} onRetry={() => void card.refetch()} />
      ) : data === undefined ? (
        <LoadingRows rows={6} />
      ) : (
        <>
          <TabProvider
            value={tab}
            onUpdate={(value) => {
              void navigate({
                search: (prev) => ({ ...prev, tab: value === 'overview' ? undefined : (value as TabId) }),
                replace: true,
              });
            }}
          >
            <TabList>
              <Tab value="overview">Обзор</Tab>
              <Tab value="tenants" counter={data.tenants.length}>
                Клиники
              </Tab>
              <Tab value="earnings">Начисления</Tab>
              <Tab value="links" counter={data.links.length}>
                Ссылки
              </Tab>
              <Tab value="actions">Действия</Tab>
            </TabList>
          </TabProvider>

          {tab === 'overview' ? <OverviewTab card={data} /> : null}
          {tab === 'tenants' ? (
            <TenantsTab
              card={data}
              onAssign={
                deleted
                  ? undefined
                  : () => {
                      setDialog('assign');
                    }
              }
            />
          ) : null}
          {tab === 'earnings' ? (
            <EarningsTab
              managerId={managerId}
              period={period}
              card={data}
              onBonus={
                deleted
                  ? undefined
                  : () => {
                      setDialog('bonus');
                    }
              }
              onCancel={setCancelling}
            />
          ) : null}
          {tab === 'links' ? <LinksTab card={data} /> : null}
          {tab === 'actions' ? <ActionsTab managerId={managerId} /> : null}
        </>
      )}

      {data === undefined ? null : (
        <>
          {dialog === 'edit' ? <ManagerFormDialog open manager={data.manager} onClose={close} /> : null}
          {dialog === 'commission' ? <CommissionDialog open card={data} onClose={close} /> : null}
          {dialog === 'bonus' ? <BonusDialog open manager={data.manager} onClose={close} /> : null}
          {dialog === 'assign' ? <AssignTenantsDialog open manager={data.manager} onClose={close} /> : null}
          {dialog === 'toggle' ? <ToggleDialog open manager={data.manager} onClose={close} /> : null}
          {dialog === 'delete' ? (
            <DeleteDialog
              open
              card={data}
              onClose={close}
              onDeleted={() => {
                close();
                go('/managers');
              }}
            />
          ) : null}
          {cancelling === null ? null : (
            <CancelBonusDialog
              open
              managerId={managerId}
              bonus={cancelling}
              onClose={() => {
                setCancelling(null);
              }}
            />
          )}
        </>
      )}
    </Page>
  );
}

// --- Обзор ---------------------------------------------------------------------

function OverviewTab({ card }: { card: ManagerCardResponse }) {
  const { stats, manager } = card;
  const conversion = stats.visits === 0 ? null : Math.round((stats.registrations / stats.visits) * 1000) / 10;

  return (
    <div className="flex flex-col gap-6">
      <MetricStrip
        metrics={[
          { label: 'Клиник за ним', value: count(stats.tenants.total), hint: `${count(stats.tenants.paying)} платят, ${count(stats.tenants.trial)} на пробном` },
          { label: 'Оплаты его клиник', value: rub(stats.revenueMinor), hint: 'за вычетом возвратов' },
          { label: 'Процент', value: rub(stats.commissionMinor), hint: `ставка сейчас ${percent(stats.commissionPercent)}` },
          { label: 'Премии', value: rub(stats.bonusesMinor) },
          { label: 'К выплате', value: rub(stats.payoutMinor), tone: 'positive' },
        ]}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Section title="По месяцам">
          <MonthChart
            title="Оплаты клиник и выплаты менеджеру по месяцам"
            months={stats.months.map((row) => row.month)}
            series={[
              {
                key: 'revenue',
                label: 'Оплаты клиник',
                color: 'var(--g-color-base-info-heavy)',
                values: stats.months.map((row) => row.revenueMinor),
              },
              {
                key: 'payout',
                label: 'К выплате',
                color: 'var(--g-color-base-brand)',
                values: stats.months.map((row) => row.commissionMinor + row.bonusesMinor),
              },
            ]}
            format={rub}
            formatAxis={rubCompact}
          />
        </Section>

        <Section title="Работа за период">
          <DefinitionList nameMaxWidth={200}>
            <DefinitionList.Item name="Переходов по ссылкам">{count(stats.visits)}</DefinitionList.Item>
            <DefinitionList.Item name="Зарегистрировались">{count(stats.registrations)}</DefinitionList.Item>
            <DefinitionList.Item name="Конверсия">{conversion === null ? 'нет данных' : percent(conversion)}</DefinitionList.Item>
            <DefinitionList.Item name="Новых клиник">{count(stats.newTenants)}</DefinitionList.Item>
            <DefinitionList.Item name="Звонков">{count(stats.activity.calls)}</DefinitionList.Item>
            <DefinitionList.Item name="Писем">{count(stats.activity.emails)}</DefinitionList.Item>
            <DefinitionList.Item name="Открыл карточек">{count(stats.activity.views)}</DefinitionList.Item>
            <DefinitionList.Item name="Требуют внимания">
              {stats.tenants.attention === 0 ? 'нет' : `${count(stats.tenants.attention)} — ждут оплаты или закрыты`}
            </DefinitionList.Item>
          </DefinitionList>
        </Section>
      </div>

      <Section title="Профиль">
        <DefinitionList nameMaxWidth={200}>
          <DefinitionList.Item name="Почта" copyText={manager.email}>
            {manager.email}
          </DefinitionList.Item>
          <DefinitionList.Item name="Телефон" {...(manager.phone === null ? {} : { copyText: manager.phone })}>
            {manager.phone ?? '—'}
          </DefinitionList.Item>
          <DefinitionList.Item name="Заведён">{day(manager.createdAt)}</DefinitionList.Item>
          <DefinitionList.Item name="Последний вход">
            {manager.lastLoginAt === null ? 'ещё не входил' : moment(manager.lastLoginAt)}
          </DefinitionList.Item>
          <DefinitionList.Item name="История процента">
            {card.commissionHistory.length === 0 ? (
              'ставка не задана — 0 %'
            ) : (
              <div className="flex flex-col gap-0.5">
                {card.commissionHistory.slice(0, 5).map((row) => (
                  <span key={row.createdAt} className="tabular">
                    {`${percent(row.percent)} с ${day(row.effectiveFrom)}`}
                    <Text as="span" color="secondary">{` · ${row.setByEmail ?? '—'}`}</Text>
                  </span>
                ))}
              </div>
            )}
          </DefinitionList.Item>
        </DefinitionList>
      </Section>
    </div>
  );
}

// --- Клиники ---------------------------------------------------------------------

function TenantsTab({ card, onAssign }: { card: ManagerCardResponse; onAssign: (() => void) | undefined }) {
  const go = useGo();
  return (
    <Section
      title="Клиники менеджера"
      flush
      actions={
        onAssign === undefined ? undefined : (
          <Button view="outlined" onClick={onAssign}>
            <Icon data={Plus} size={16} />
            Закрепить клиники
          </Button>
        )
      }
    >
      {card.tenants.length === 0 ? (
        <div className="px-5">
          <Empty
            title="За менеджером пока нет клиник"
            description="Клиники закрепляются сами, когда регистрируются по его ссылке, — или их закрепляет сотрудник платформы."
          />
        </div>
      ) : (
        <div>
          <Table
            className="w-full"
            edgePadding
            stickyHorizontalScroll
            data={card.tenants}
            getRowDescriptor={(row) => ({ id: row.id, interactive: true })}
            onRowClick={(row, _index, event) => {
              if ((event.target as HTMLElement).closest('a') !== null) return;
              go(`/tenants/${row.id}`);
            }}
            columns={[
              { id: 'name', name: 'Клиника', template: (row) => <RouterLink to={`/tenants/${row.id}`}>{row.name}</RouterLink> },
              { id: 'kind', name: 'Тип', template: (row) => KIND_LABEL[row.kind] },
              {
                id: 'state',
                name: 'Подписка',
                template: (row) => <Label theme={STATE_TONE[row.subscriptionState]}>{STATE_LABEL[row.subscriptionState]}</Label>,
              },
              {
                id: 'since',
                name: 'За ним с',
                template: (row) => (
                  <span className="flex flex-col">
                    <span className="tabular">{day(row.since)}</span>
                    <Text variant="caption-2" color="secondary">
                      {row.source === 'referral' ? 'по ссылке' : 'закрепил сотрудник'}
                    </Text>
                  </span>
                ),
              },
              {
                id: 'revenue',
                name: 'Оплаты',
                align: 'end',
                template: (row) => <span className="tabular">{rub(row.revenueMinor)}</span>,
              },
              {
                id: 'commission',
                name: 'Процент',
                align: 'end',
                template: (row) => <span className="tabular">{rub(row.commissionMinor)}</span>,
              },
            ]}
          />
        </div>
      )}
    </Section>
  );
}

// --- Начисления ----------------------------------------------------------------------

function EarningsTab({
  managerId,
  period,
  card,
  onBonus,
  onCancel,
}: {
  managerId: string;
  period: Period;
  card: ManagerCardResponse;
  onBonus: (() => void) | undefined;
  onCancel: (bonus: ManagerBonusView) => void;
}) {
  const api = useManagersApi();
  const earnings = useQuery({
    queryKey: ['manager-earnings', managerId, period.from, period.to],
    queryFn: () => api.earnings(managerId, period),
  });

  return (
    <div className="flex flex-col gap-6">
      {earnings.isError ? (
        <ErrorNote title="Начисления не загрузились" error={earnings.error} onRetry={() => void earnings.refetch()} />
      ) : (
        <MetricStrip
          loading={earnings.data === undefined}
          metrics={[
            { label: 'Оплаты клиник', value: rub(earnings.data?.totals.revenueMinor ?? 0) },
            { label: 'Процент', value: rub(earnings.data?.totals.commissionMinor ?? 0) },
            { label: 'Премии', value: rub(earnings.data?.totals.bonusesMinor ?? 0) },
            { label: 'К выплате', value: rub(earnings.data?.totals.payoutMinor ?? 0), tone: 'positive' },
          ]}
        />
      )}

      <Section title="С оплат клиник" flush>
        {earnings.data === undefined ? (
          <div className="px-5 pb-3">
            <LoadingRows rows={4} height={32} />
          </div>
        ) : earnings.data.accruals.length === 0 ? (
          <div className="px-5">
            <Empty title="За период оплат не было" description="Процент начисляется с каждой оплаты подписки клиник менеджера." />
          </div>
        ) : (
          <div>
            <Table
              className="w-full"
              edgePadding
            stickyHorizontalScroll
              data={earnings.data.accruals}
              getRowDescriptor={(row) => ({ id: row.paymentId })}
              columns={[
                { id: 'at', name: 'Оплата', template: (row) => <span className="tabular">{moment(row.settledAt)}</span> },
                { id: 'tenant', name: 'Клиника', template: (row) => <RouterLink to={`/tenants/${row.tenantId}`}>{row.tenantName}</RouterLink> },
                { id: 'kind', name: 'Тип', template: (row) => paymentKind(row.kind) },
                { id: 'amount', name: 'Сумма', align: 'end', template: (row) => <span className="tabular">{rub(row.amountMinor)}</span> },
                { id: 'percent', name: 'Ставка', align: 'end', template: (row) => <span className="tabular">{percent(row.percent)}</span> },
                {
                  id: 'commission',
                  name: 'Процент',
                  align: 'end',
                  template: (row) => <span className="tabular">{rub(row.commissionMinor)}</span>,
                },
              ]}
            />
          </div>
        )}
      </Section>

      <Section
        title="Премии"
        flush
        actions={
          onBonus === undefined ? undefined : (
            <Button view="outlined" onClick={onBonus}>
              <Icon data={Gift} size={16} />
              Назначить премию
            </Button>
          )
        }
      >
        {card.bonuses.length === 0 ? (
          <div className="px-5">
            <Empty title="Премий не было" />
          </div>
        ) : (
          <div>
            <Table
              className="w-full"
              edgePadding
            stickyHorizontalScroll
              data={card.bonuses}
              getRowDescriptor={(row) => ({ id: row.id })}
              columns={[
                { id: 'month', name: 'За месяц', template: (row) => monthTitle(row.month) },
                {
                  id: 'amount',
                  name: 'Сумма',
                  align: 'end',
                  template: (row) => (
                    <span className={`tabular ${row.cancelledAt === null ? '' : 'line-through opacity-60'}`}>{rub(row.amountMinor)}</span>
                  ),
                },
                {
                  id: 'comment',
                  name: 'За что',
                  template: (row) => (
                    <span className="flex flex-col">
                      <span>{row.comment}</span>
                      {row.cancelledAt === null ? null : (
                        <Text variant="caption-2" color="danger">
                          {`Отменена ${day(row.cancelledAt)}: ${row.cancelReason ?? ''}`}
                        </Text>
                      )}
                    </span>
                  ),
                },
                {
                  id: 'by',
                  name: 'Назначил',
                  template: (row) => (
                    <Text color="secondary">{`${row.createdByEmail ?? '—'} · ${day(row.createdAt)}`}</Text>
                  ),
                },
                {
                  id: 'actions',
                  name: '',
                  align: 'end',
                  template: (row) =>
                    row.cancelledAt !== null || onBonus === undefined ? null : (
                      <Button
                        view="flat-danger"
                        size="s"
                        onClick={() => {
                          onCancel(row);
                        }}
                      >
                        Отменить
                      </Button>
                    ),
                },
              ]}
            />
          </div>
        )}
      </Section>
    </div>
  );
}

// --- Ссылки ------------------------------------------------------------------------------

function LinksTab({ card }: { card: ManagerCardResponse }) {
  return (
    <Section title="Реферальные ссылки" flush>
      {card.links.length === 0 ? (
        <div className="px-5">
          <Empty
            title="Ссылок пока нет"
            description="Менеджер заводит их сам в своём кабинете — по одной на источник, чтобы видеть, откуда пришли клиники."
          />
        </div>
      ) : (
        <div>
          <Table
            className="w-full"
            edgePadding
            stickyHorizontalScroll
            data={card.links}
            getRowDescriptor={(row) => ({ id: row.id })}
            columns={[
              {
                id: 'url',
                name: 'Ссылка',
                template: (row) => (
                  <span className="flex items-center gap-1.5">
                    <Icon data={LinkIcon} size={14} className="text-[var(--g-color-text-secondary)]" />
                    <span className="break-all">{row.url}</span>
                    <ClipboardButton text={row.url} size="xs" />
                  </span>
                ),
              },
              { id: 'label', name: 'Подпись', template: (row) => row.label ?? <Text color="secondary">—</Text> },
              { id: 'visits', name: 'Переходов', align: 'end', template: (row) => <span className="tabular">{count(row.visits)}</span> },
              { id: 'recent', name: 'За 30 дней', align: 'end', template: (row) => <span className="tabular">{count(row.visits30d)}</span> },
              {
                id: 'registrations',
                name: 'Регистраций',
                align: 'end',
                template: (row) => <span className="tabular">{count(row.registrations)}</span>,
              },
              {
                id: 'state',
                name: 'Состояние',
                template: (row) =>
                  row.disabledAt === null ? <Label theme="success">Работает</Label> : <Label theme="unknown">Выключена</Label>,
              },
            ]}
          />
        </div>
      )}
    </Section>
  );
}

// --- Действия ------------------------------------------------------------------------------

function ActionsTab({ managerId }: { managerId: string }) {
  const api = useManagersApi();
  const query = useInfiniteQuery({
    queryKey: ['manager-audit', managerId, undefined, undefined, undefined],
    queryFn: ({ pageParam }) => api.audit({ managerId, limit: 30, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const entries = query.data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <Section title="Журнал" flush actions={<RouterLink to={`/managers/audit?managerId=${managerId}`}>Открыть с фильтрами</RouterLink>}>
      {query.isError ? (
        <div className="px-5 pb-4">
          <ErrorNote title="Журнал не загрузился" error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : query.isPending ? (
        <div className="px-5 pb-3">
          <LoadingRows rows={5} height={44} />
        </div>
      ) : entries.length === 0 ? (
        <div className="px-5">
          <Empty title="Записей пока нет" />
        </div>
      ) : (
        <>
          <AuditFeed entries={entries} showManager={false} />
          {query.hasNextPage ? (
            <div className="flex justify-center px-5 pt-2 pb-3">
              <Button view="outlined" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                Показать ещё
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}
